import { parseCron } from "@avenueops/frontend-shared";
import { DropdownSelect, SmartForm } from "@avenueops/web-components";
import { WorkflowFrequency } from "@prisma/client";
import moment from "moment-timezone";
import { Controller, useFormContext } from "react-hook-form";

const frequencyChoices = [
  { label: "every 30 seconds", value: WorkflowFrequency.THIRTYSECS },
  { label: "every 5 minutes", value: WorkflowFrequency.FIVEMIN, selected: true },
  { label: "hourly", value: WorkflowFrequency.HOURLY },
  { label: "daily", value: WorkflowFrequency.DAILY },
  { label: "weekly", value: WorkflowFrequency.WEEKLY },
  { label: "using cron expression", value: WorkflowFrequency.CUSTOM_CRON },
];
const timezoneChoices = moment.tz.names().map((timezone) => ({ label: timezone, value: timezone }));

export default function MonitorCadenceSelector(): JSX.Element {
  const { watch, formState } = useFormContext();

  const days = ["Mon", "Tues", "Weds", "Thurs", "Fri", "Sat", "Sun"];

  const frequency = watch("frequency");

  const hours = Array.from(Array(24).keys()).map((index) => {
    return index === 0
      ? { label: `12:00 AM`, value: `${index}` }
      : index === 12
      ? { label: `12:00 PM`, value: `${index}` }
      : { label: `${index > 12 ? index - 12 : index}:00 ${index > 12 ? "PM" : "AM"}`, value: `${index}` };
  });

  return (
    <div className="w-full">
      <div className="w-full flex flex-row pb-4">
        <div className="flex-1 items-center">
          <div className="text-lg font-medium text-gray-900">Check for alerts</div>
          <div className="text-sm text-gray-600 font-normal">How often should we check your data?</div>
        </div>
        <div className="flex-1 items-center">
          <SmartForm.DropdownSelect name="frequency" placeholder="UTC" options={frequencyChoices} />
        </div>
      </div>
      <div className="w-full">
        {frequency === WorkflowFrequency.DAILY || frequency === WorkflowFrequency.WEEKLY ? (
          <div className="items-center flex flex-row w-full justify-between gap-8">
            <div className="text-gray-900 font-medium text-sm"> at </div>
            <div className="flex-1">
              <Controller
                name="hour"
                render={({ field }) => (
                  <DropdownSelect
                    name="hour"
                    placeholder="UTC"
                    options={hours as any}
                    selectedValue={field.value?.toString()}
                    onChange={(val) => field.onChange(Number(val))}
                  />
                )}
              />
            </div>

            <div className="text-gray-900 font-medium text-sm"> in timezone</div>
            <div className="flex-1">
              <SmartForm.DropdownSelect name="timezone" placeholder="UTC" options={timezoneChoices} />
            </div>
          </div>
        ) : undefined}
        {frequency === WorkflowFrequency.WEEKLY ? (
          <div className="pt-4 flex flex-row items-center gap-8">
            <div className="text-gray-900 font-medium text-sm"> on </div>
            <SmartForm.CheckboxGroup name="days" options={days.map((day) => ({ label: day, value: day }))} />
          </div>
        ) : undefined}
        {frequency === WorkflowFrequency.CUSTOM_CRON ? (
          <div className="w-full items-center flex flex-row gap-8">
            <div className="flex-1">
              <SmartForm.TextInput name="cronExpression" placeholder="e.g. * * * * *" />
            </div>
            <div className="text-gray-900 text-sm font-medium h-10 flex items-center gap-x-2">in timezone</div>
            <div className="flex-1">
              <SmartForm.DropdownSelect name="timezone" placeholder="UTC" options={timezoneChoices} />
            </div>
          </div>
        ) : undefined}
      </div>
    </div>
  );
}
